<template>
  <v-card id="msg-list" flat>
    <v-tabs-items v-model="computedTabs">
      <v-tab-item>
        <v-row v-if="isCampaign" class="mt-2" no-gutters>
          <v-col class="pa-2" cols="12" lg="3" md="4">
            <v-text-field
              v-model="campName"
              class="rounded-lg"
              clear-icon="mdi-close-circle-outline"
              clearable
              dense
              label="نام کمپین"
              outlined
            />
          </v-col>
        </v-row>
        <div
          class="d-flex justify-space-between align-center my-3"
          v-if="computedValue?.length > 1"
        >
          <div class="bold">
            {{ isCampaign ? "متن‌های کمپین" : "پیام‌ها" }}
            <span class="grey--text"
              >({{ persian(computedValue?.length) }})</span
            >
          </div>

          <v-btn
            :class="
              !isReorderMode ? 'btn-edit rounded-lg' : 'btn-accept rounded-lg'
            "
            outlined
            small
            @click="isReorderMode = !isReorderMode"
          >
            <v-icon left small>{{
              isReorderMode ? "mdi-check" : "mdi-arrow-up-down-bold"
            }}</v-icon>
            {{ isReorderMode ? "ذخیره ترتیب" : "تغییر ترتیب" }}
          </v-btn>
        </div>
        <div
          v-if="computedValue?.length === 0 && isCampaign"
          class="text-center pa-8 grey--text"
        >
          <v-icon large color="grey lighten-1">mdi-information</v-icon>
          <div class="mt-4">
            برای شروع کمپین، از دکمه "افزودن متن جدید" استفاده کنید.
          </div>
        </div>
        <draggable
          v-model="computedValue"
          tag="div"
          animation="300"
          :disabled="loading"
          ghost-class="ghost"
          chosen-class="chosen"
          drag-class="drag"
          @start="onDragStart"
          @end="onDragEnd"
          handle=".handle"
        >
          <div
            v-for="(item, i) in computedValue"
            :key="i"
            class="draggable-item"
          >
            <campaign-list-item
              :index="i"
              :item="item"
              :loading="i === 0 && loading"
              :show-delete="computedValue.length > 1 && !isReorderMode"
              :show-reorder="computedValue.length > 1"
              :title-info="getItemTitle(item)"
              :total-items="computedValue.length"
              :reorder-mode="isReorderMode"
              @deleteRow="deleteRow(i)"
              @moveUp="moveItemUp"
              @moveDown="moveItemDown"
              :class="{ 'reorder-item': isReorderMode, 'mb-3': !isReorderMode }"
              :message-types="messageTypes"
              class="handle"
            />
          </div>
        </draggable>
      </v-tab-item>

      <v-tab-item disabled>
        <campaign-list-item
          v-for="(item, i) in computedValue"
          :key="item.__uniqueId"
          :index="i"
          :item="item"
          :loading="i === 0 && loading"
          :show-delete="computedValue.length > 1"
          :title="isCampaign ? 'متن' : 'پیام'"
          tracking
          @deleteRow="deleteRow(i)"
        />
      </v-tab-item>

      <v-tab-item>
        <v-card :loading="campLoading" class="px-3 pb-3 rounded-lg" outlined>
          <v-card-title class="font-size-h6 pa-2">
            برای
            <span class="red--text mx-1">انتخاب کمپین</span>
            روی ردیف مورد نظر کلیک کنید.
          </v-card-title>

          <data-table
            :columns="columns"
            :data="data"
            :orderDir="tableProps.dir"
            :per-page="[10, 25, 50, 100, 400]"
            :translate="translate"
            class="mt-3"
            @on-table-props-changed="reloadTable"
            @row-clicked="selectRow"
          />
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <v-card-actions v-if="sendPagesTab !== 2 && !stage" class="px-0">
      <v-btn
        block
        class="rounded-lg btn-send-sms ma-0"
        depressed
        width="100%"
        @click="addRow('sms_text', 'btn')"
      >
        <v-icon left>mdi-plus</v-icon>
        افزودن
        <span class="px-1">{{ isCampaign ? "متن" : "پیام" }}</span>
        جدید
      </v-btn>
    </v-card-actions>

    <slot v-if="isCampaign && !stage" name="submit">
      <v-card-actions class="px-0 justify-end">
        <v-btn
          :disabled="!canMore"
          :loading="loading"
          class="btn-send-sms"
          @click="addCamp"
        >
          <span class="flaticon2-send-1 icon-md"></span>
          ثبت کمپین
        </v-btn>
      </v-card-actions>
    </slot>
  </v-card>
</template>

<script>
import CampaignListItem from "./campaignListItem.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { debounce, SmsAxios } from "@/main";

export default {
  name: "messageList",

  components: { CampaignListItem },

  props: {
    campId: Number,
    isCampaign: Boolean,
    value: Array,
    title: {
      type: String,
      default: function () {
        return this.$route.meta.title;
      },
    },
    loading: Boolean,
    stage: Boolean,
    srcObj: Object,
  },

  data() {
    return {
      uniqueCounter: 0,
      messageTypes: [
        {
          text: "پیامک متنی",
          value: "sms_text",
          icon: "/media/socialIcon/sms.webp",
          disabled: false,
        },
      ],
      dragging: false,
      isReorderMode: false,
      campLoading: false,
      data: {},
      campName: "",
      columns: [
        {
          label: "ردیف",
          transform: (val) => {
            return this.rowNumber(this.data, val);
          },
        },
        {
          label: "شناسه",
          name: "id",
          orderable: true,
        },
        {
          label: "تاریخ",
          name: "created_at",
          transform: ({ data }) => {
            return this.datetime(data.created_at);
          },
        },
        {
          label: "ساعت",
          name: "created_at_time",
          transform: ({ data }) => {
            return this.getTime(data.created_at);
          },
        },
        {
          label: "تعداد پیام",
          name: "text",
          transform: ({ data }) => {
            return data.messages.text.length || 0;
          },
        },
        {
          label: "مجموع پیام (صفحه)",
          name: "count",
          transform: ({ data }) => {
            return data.messages.count || "-" + " صفحه";
          },
        },
        { label: "نام کمپین", name: "messages.name", serchable: true },
      ],
      tableProps: {
        search: "",
        filters: {},
        status: "accept",
        length: 10,
        column: "id",
        dir: "desc",
      },
      translate: {
        nextButton: "بعدی",
        previousButton: "قبلی",
        rowsPerPageText: "تعداد در صفحه",
        noDataText: "داده ای برای نمایش وجود ندارد",
        placeholderSearch: "جستجو",
      },
    };
  },

  created() {
    this.campName = this.srcObj?.messages?.name;
    this.fetchCamps(this.tableProps);
    if (this.computedValue && Array.isArray(this.computedValue)) {
      this.computedValue.forEach((item) => {
        if (item && !item.__uniqueId) {
          this.$set(item, "__uniqueId", ++this.uniqueCounter);
        }
      });
    }
    if (!this.computedValue || this.computedValue.length === 0) {
      this.addRow("sms_text");
    }
  },

  computed: {
    ...mapState("locals", ["sendPagesTab"]),
    ...mapGetters("locals", ["sendTextPattern"]),
    isAllRowsValid() {
      if (!this.value || !this.value.length) return false;
      return this.value.every((item, index) => {
        const hasTitle = !!item.title?.trim();
        const hasOperator = !!(item.Operator || item.OperatorDisplay);
        const hasTiming = this.isCampaign
          ? index === 0 || (item.gap != null && item.gap >= 0)
          : !!item.date?.trim();

        const contentValid =
          !!item.text?.replace("\nلغو11", "").trim() &&
          item.text.includes(this.sendTextPattern);

        const linkOk = !item.link || this.isDomain(item.link);
        return hasTitle && hasOperator && hasTiming && contentValid && linkOk;
      });
    },
    canMore() {
      if (!this.value || !this.value.length) return false;
      return (
        this.isAllRowsValid &&
        (!this.isCampaign || this.campName?.trim().length >= 3)
      );
    },
    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    computedTabs() {
      return this.sendPagesTab;
    },
  },

  watch: {
    computedValue: {
      deep: true,
      handler(val) {
        if (val && Array.isArray(val)) {
          val.forEach((item) => {
            if (item && !item.__uniqueId) {
              this.$set(item, "__uniqueId", ++this.uniqueCounter);
            }
          });
        }

        if (!this.campId && val.length > 0) {
          this.SetSendOperator(val[0]?.Operator);
        }
      },
    },
    computedTabs(val) {
      if (!val)
        this.computedValue = [
          {
            __uniqueId: ++this.uniqueCounter,
            title: this.$route.meta.title,
            date: this.nowDateTimeString(),
            text: "\nلغو11",
            lang: "",
            msgCount: 0,
            messageType: "sms_text",
          },
        ];
      else if (val === 1)
        this.computedValue = [
          {
            __uniqueId: ++this.uniqueCounter,
            title: "لینک قابل رهگیری",
            date: this.nowDateTimeString(),
            link: "",
            text: "\nexample.ex/any\nلغو11",
            lang: "",
            msgCount: 0,
            messageType: "sms_text",
          },
        ];
      else {
        this.computedValue = [];
        this.reloadTable(this.tableProps);
      }
    },
    srcObj(val) {
      val && (this.campName = val.messages?.name);
    },
  },

  methods: {
    ...mapActions("locals", ["SetSendTabs", "SetSendOperator"]),

    getItemTitle() {
      return {
        text: `متن پیامک`,
        icon: "/media/socialIcon/sms.webp",
      };
    },

    fetchCamps(params = this.tableProps) {
      this.$emit("update:loading", true);
      this.campLoading = true;

      SmsAxios.get("campaign/list", { params })
        .then(({ data }) => (this.data = data))
        .catch(({ response }) => console.log("error in res: ", response))
        .finally(() => {
          this.$emit("update:loading", false);
          this.campLoading = false;
        });
    },
    reloadTable(props) {
      debounce(() => this.fetchCamps(props), 500);
    },
    selectRow(data) {
      let arr = [];
      const messages = data.messages;

      (messages?.messageType || ["sms_text"]).forEach((msgType, i) => {
        this.$toast.clear();

        if (!data.messages[i]) {
          return this.$toast.warning(
            "لطفا متن/های کمپین انتخابی خود را تصحیح نمایید!",
            { timeout: 4000 },
          );
        } else if (!data.messages.title[i]) {
          return this.$toast.warning(
            "لطفا عنوان/های کمپین انتخابی خود را تصحیح نمایید!",
            { timeout: 4000 },
          );
        } else if (!(data.messages.gap[i] >= 0)) {
          return this.$toast.warning(
            "لطفا فاصله زمانی/های کمپین انتخابی خود را تصحیح نمایید!",
            { timeout: 4000 },
          );
        } else if (!data.messages.Operator || !data.messages.Operator[i]) {
          return this.$toast.warning(
            "لطفا اپراتور/های کمپین انتخابی خود را تصحیح نمایید!",
            { timeout: 4000 },
          );
        }

        let item = {
          __uniqueId: ++this.uniqueCounter,
          title: messages.title?.[i] || "",
          gap: i === 0 ? 0 : messages.gap?.[i] ?? 0,
          type: messages.type?.[i] || "hour",
          Operator:
            messages.Operator?.[i] || messages.OperatorDisplay?.[i] || "",
          OperatorDisplay: messages.OperatorDisplay?.[i] || "",
          messageType: "sms_text",
          text: messages.text?.[i] || "",
        };

        if (!item.title) {
          this.$toast.warning(`عنوان پیام ${i + 1} خالی است`);
        }

        arr.push(item);
      });
      this.computedValue = arr;
      this.$emit("update:campId", data.id);

      const id = data.id;
      $("tr").css("background", "");

      $(`tr:contains("${id}")`).each(function (index, item) {
        if ($(item).find("td").eq(1).text() == id)
          $(item).css("background", "rgba(99, 0, 244, 0.28)");
      });
    },
    addRow(typemessage = "sms_text", startat = "menu") {
      if (this.value && this.value?.length > 0 && !this.isAllRowsValid) {
        if (startat === "btn") {
          let message =
            this.value?.length === 1
              ? "لطفاً ابتدا اطلاعات پیام‌ قبلی را به طور کامل پر کنید."
              : "لطفاً ابتدا اطلاعات پیام‌های قبلی را به طور کامل پر کنید.";

          this.$toast.error(message);
        }
        return;
      }

      let obj = {
        __uniqueId: ++this.uniqueCounter,
        title: `${this.isCampaign ? "متن" : this.title} ${
          this.value?.length + 1 || 2
        }`,
        [this.isCampaign ? "gap" : "date"]: this.isCampaign
          ? this.value?.length
            ? null
            : 0
          : "",
        text: "\n" + this.sendTextPattern,
        messageType: "sms_text",
        type: "hour",
        Operator: this.value?.length
          ? this.value[this.value?.length - 1]?.Operator || ""
          : "",
        OperatorDisplay: this.value?.length
          ? this.value[this.value?.length - 1]?.OperatorDisplay || ""
          : "",
      };

      if (this.sendPagesTab === 1) {
        obj.link = "";
      }

      if (this.value?.length >= 10)
        return this.$toast.error("حداکثر 10 مورد میتوان اضافه نمود.");

      if (!this.value) {
        this.computedValue = [obj];
      } else {
        this.computedValue.push(obj);
      }
    },
    deleteRow(index) {
      this.computedValue.splice(index, 1);
      if (this.isCampaign && index === 0) this.computedValue[0].gap = 0;
    },
    async addCamp() {
      let campaignData = {
        name: this.campName.trim(),
        title: [],
        gap: [],
        type: [],
        Operator: [],
        OperatorDisplay: [],
        messageType: [],
        count: 0,
        text: [],
      };

      for (const item of this.computedValue) {
        campaignData.title.push(item.title?.trim() || "");
        campaignData.gap.push(item.gap ?? 0);
        campaignData.type.push(item.type || "hour");

        campaignData.Operator.push(
          this.ribbon_can("all_numbers") && item.Operator?.trim()
            ? item.Operator
            : item.OperatorDisplay?.trim() || null,
        );
        campaignData.OperatorDisplay.push(item.OperatorDisplay || "");
        campaignData.messageType.push("sms_text");
        campaignData.count += item.msgCount || 0;
        campaignData.text.push(item.text?.trim() || "");
      }

      if (!campaignData.name) {
        this.$toast.error("نام کمپین را وارد کنید");
        return;
      }

      this.$emit("submit", { campaign: campaignData });
    },

    isDomain(str) {
      var regex = new RegExp(
        "^(http[s]?:\\/\\/(www\\.)?|ftp:\\/\\/(www\\.)?|www\\.){1}([0-9A-Za-z-\\.@:%_\+~#=]+)+((\\.[a-zA-Z]{2,3})+)(/(.)*)?(\\?(.)*)?",
      );
      return !str || regex.test(str) || str.length <= 0;
    },
    onDragStart() {
      this.dragging = true;
    },
    onDragEnd() {
      this.dragging = false;
    },
    moveItemUp(index) {
      if (index <= 0) return;

      const items = [...this.computedValue];
      [items[index - 1], items[index]] = [items[index], items[index - 1]];
      this.computedValue = items;
    },

    moveItemDown(index) {
      if (index >= this.computedValue?.length - 1) return;

      const items = [...this.computedValue];
      [items[index], items[index + 1]] = [items[index + 1], items[index]];
      this.computedValue = items;
    },
  },

  beforeDestroy() {
    this.SetSendTabs(0);
  },
};
</script>
<style scoped>
.draggable-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  width: 100%;
}

.handle {
  cursor: grab;
  margin-left: 10px;
}
</style>
